import { io, Socket } from 'socket.io-client';
import {
   collection,
   query,
   where,
   getDocs,
   orderBy,
   limit as firestoreLimit,
   doc,
   deleteDoc,
   getDoc,
   addDoc,
   setDoc,
   serverTimestamp,
   startAfter,
   updateDoc,
} from 'firebase/firestore';
import { db } from '../firebase/config';

// TypeScript interfaces for type safety
export interface ChatMessage {
   id: string;
   senderId: string;
   senderName: string;
   text: string;
   timestamp: Date;
   roomId: string;
   edited?: boolean;
   editedAt?: Date;
   reactions?: { [emoji: string]: string[] }; // emoji -> array of user IDs
}

export interface ChatRoom {
   id: string;
   name: string;
   description?: string;
   createdBy: string;
   createdAt: Date;
   members: string[];
   isPrivate: boolean;
   lastMessage?: ChatMessage;
   unreadCount?: number;
   avatarUrl?: string; // Avatar URL based on room type (user profile, event cover, trip cover)
}

// New interface for paginated room results
export interface PaginatedRoomsResult {
   rooms: ChatRoom[];
   hasMore: boolean;
   lastDoc?: any; // Firestore document snapshot for cursor
   totalCount?: number;
}

export interface UserStatus {
   userId: string;
   username: string;
   isOnline: boolean;
   lastSeen?: Date;
   currentRoom?: string;
}

export interface TypingIndicator {
   userId: string;
   username: string;
   roomId: string;
}

export interface WebSocketEvents {
   // Connection events
   connect: () => void;
   disconnect: (reason: string) => void;
   error: (error: Error) => void;

   // Reconnection events
   reconnecting: (attemptNumber: number) => void;
   reconnected: (attemptNumber: number) => void;
   reconnect_error: (error: Error) => void;
   reconnect_failed: () => void;
   max_reconnect_attempts_reached: () => void;

   // User events
   'user-joined': (user: UserStatus) => void;
   'user-left': (userId: string) => void;
   'user-status-changed': (status: UserStatus) => void;

   // Room events
   'room-created': (room: ChatRoom) => void;
   'joined-room': (room: ChatRoom) => void;
   'left-room': (roomId: string) => void;

   // Message events
   'new-message': (message: ChatMessage) => void;
   'message-edited': (message: ChatMessage) => void;
   'message-deleted': (messageId: string, roomId: string) => void;
   'message-reaction': (messageId: string, emoji: string, userId: string, added: boolean) => void;

   // Typing events
   'user-typing': (indicator: TypingIndicator) => void;
   'user-stopped-typing': (userId: string, roomId: string) => void;

   // Group chat notifications
   'group-chat-notification': (notification: {
      type: 'group_chat_created' | 'group_chat_updated';
      entityType: 'event' | 'trip';
      entityName: string;
      roomId: string;
      createdBy: string;
      members: string[];
      timestamp: string;
   }) => void;

   // Real-time notifications
   'event-trip-notification': (notification: {
      notificationId: string;
      senderId: string;
      senderUsername: string;
      senderDisplayName: string;
      type: string;
      title: string;
      message: string;
      data?: Record<string, any>;
      priority: string;
      category: string;
      actionUrl?: string;
      timestamp: Date;
      isRead: boolean;
      recipientId?: string;
      userId?: string;
      createdAt: Date;
   }) => void;

   // Loading state events
   'loading-state-changed': (loadingStates: {
      sendingMessage: { [roomId: string]: boolean };
      addingReaction: { [messageId: string]: { [emoji: string]: boolean } };
      removingReaction: { [messageId: string]: { [emoji: string]: boolean } };
   }) => void;

   // Network status events
   network_status_changed: (status: { isOnline: boolean; supported: boolean }) => void;

   // New chat notification events
   'new-chat-notification': (notification: {
      type: 'message';
      title: string;
      message: string;
      emoji: string;
      subtitle: string;
      data?: any;
      priority: 'high';
      actions: { label: string; action: () => void }[];
   }) => void;

   // New chat notification acknowledgment events
   'new-chat-notification-sent': (data: any) => void;
   'new-chat-notification-error': (error: any) => void;
}

class WebSocketService {
   private socket: Socket | null = null;
   public isConnected = false;
   private currentUser: { id: string; name: string } | null = null;
   private eventListeners: Map<string, Function[]> = new Map();
   private messages: { [roomId: string]: ChatMessage[] } = {};

   // Message processing tracking
   private processedMessageIds: Set<string> = new Set();
   private connectionAttempts = 0;
   private maxConnectionAttempts = 5; // Increased from 3

   // Enhanced connection state
   private serverUrl: string = '';
   private reconnectAttempts = 0;
   private maxReconnectAttempts = 10;
   private reconnectDelay = 1000; // Start with 1 second
   private maxReconnectDelay = 30000; // Max 30 seconds
   private reconnectTimer: NodeJS.Timeout | null = null;
   private joinedRooms: Set<string> = new Set(); // Track joined rooms for restoration
   private isReconnecting = false;
   private autoReconnectEnabled = true; // New flag to control auto-reconnect
   private connectionHealthCheckInterval: NodeJS.Timeout | null = null;
   private lastPongReceived: number = 0;
   private pingTimeout: NodeJS.Timeout | null = null;
   private isManualDisconnect = false; // Track if disconnect was intentional

   // Online/offline status tracking
   private isOnline = true; // Track network connectivity
   private onlineStatusInitialized = false;

   // Status update throttling
   private lastStatusUpdate: { [userId: string]: { isOnline: boolean; timestamp: number } } = {};
   private statusUpdateThrottle = 5000; // 5 seconds throttling

   // Loading states
   private sendingMessages: Set<string> = new Set(); // Track messages being sent by room ID
   private processingReactions: Set<string> = new Set(); // Track reactions being processed by messageId_emoji
   private loadingStates: {
      sendingMessage: { [roomId: string]: boolean };
      addingReaction: { [messageId: string]: { [emoji: string]: boolean } };
      removingReaction: { [messageId: string]: { [emoji: string]: boolean } };
   } = {
      sendingMessage: {},
      addingReaction: {},
      removingReaction: {},
   };

   // Notification handler
   private notificationHandler: ((notification: any) => void) | null = null;

   constructor() {
      this.initializeOnlineStatusTracking();
   }

   // Initialize online/offline status tracking
   private initializeOnlineStatusTracking(): void {
      if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
         // Set initial online status
         this.isOnline = navigator.onLine;

         // Set up online/offline event listeners
         const handleOnline = () => {
            console.log('🌐 Network status: ONLINE');
            this.isOnline = true;
            this.emit('network_status_changed', { isOnline: true });

            // If we were disconnected due to being offline, try to reconnect
            if (
               !this.isConnected &&
               this.autoReconnectEnabled &&
               !this.isReconnecting &&
               !this.isManualDisconnect
            ) {
               console.log('🔄 Network back online, attempting to reconnect...');
               this.scheduleReconnection();
            }
         };

         const handleOffline = () => {
            console.log('🌐 Network status: OFFLINE');
            this.isOnline = false;
            this.emit('network_status_changed', { isOnline: false });

            // Cancel any pending reconnection attempts when going offline
            if (this.reconnectTimer) {
               clearTimeout(this.reconnectTimer);
               this.reconnectTimer = null;
               console.log('⏰ Cancelled reconnection attempts due to offline status');
            }
         };

         window.addEventListener('online', handleOnline);
         window.addEventListener('offline', handleOffline);

         this.onlineStatusInitialized = true;
         console.log(
            `🌐 Online status tracking initialized. Current status: ${this.isOnline ? 'ONLINE' : 'OFFLINE'}`
         );
      }
   }

   // Get current network status
   getNetworkStatus(): { isOnline: boolean; supported: boolean } {
      return {
         isOnline: this.isOnline,
         supported: this.onlineStatusInitialized,
      };
   }

   // Register notification handler
   setNotificationHandler(handler: (notification: any) => void): void {
      this.notificationHandler = handler;
   }

   // Remove notification handler
   removeNotificationHandler(): void {
      this.notificationHandler = null;
   }

   // Enable or disable auto-reconnect
   setAutoReconnectEnabled(enabled: boolean): void {
      this.autoReconnectEnabled = enabled;
      if (!enabled && this.reconnectTimer) {
         clearTimeout(this.reconnectTimer);
         this.reconnectTimer = null;
      }
   }

   // Start connection health monitoring
   private startConnectionHealthCheck(): void {
      if (this.connectionHealthCheckInterval) {
         clearInterval(this.connectionHealthCheckInterval);
      }

      this.connectionHealthCheckInterval = setInterval(() => {
         if (this.socket && this.isConnected) {
            // Send ping to server
            this.socket.emit('ping');

            // Set timeout for pong response
            if (this.pingTimeout) {
               clearTimeout(this.pingTimeout);
            }

            this.pingTimeout = setTimeout(() => {
               console.warn('⚠️ No pong received from server, connection may be unhealthy');
               if (this.autoReconnectEnabled && !this.isReconnecting) {
                  this.handleConnectionLoss();
               }
            }, 5000); // 5 second timeout for pong
         }
      }, 15000); // Check every 15 seconds
   }

   // Stop connection health monitoring
   private stopConnectionHealthCheck(): void {
      if (this.connectionHealthCheckInterval) {
         clearInterval(this.connectionHealthCheckInterval);
         this.connectionHealthCheckInterval = null;
      }

      if (this.pingTimeout) {
         clearTimeout(this.pingTimeout);
         this.pingTimeout = null;
      }
   }

   // Handle connection loss
   private handleConnectionLoss(): void {
      if (this.isManualDisconnect) {
         console.log('🔌 Manual disconnect detected, skipping auto-reconnect');
         return;
      }

      // Check if we're offline before attempting reconnection
      if (!this.isOnline) {
         console.log('🌐 Device is offline, skipping reconnection until network is restored');
         this.isConnected = false;
         this.emit('disconnect', 'Network offline');
         return;
      }

      this.isConnected = false;
      this.emit('disconnect', 'Connection lost');

      if (this.autoReconnectEnabled) {
         this.scheduleReconnection();
      }
   }

   // Connect to WebSocket server
   async connect(
      userId: string,
      userName: string,
      serverUrl = process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:4000'
   ): Promise<void> {
      return new Promise((resolve, reject) => {
         if (this.isConnected && this.socket) {
            resolve();
            return;
         }

         this.isManualDisconnect = false;
         this.currentUser = { id: userId, name: userName };
         this.serverUrl = serverUrl;
         this.connectionAttempts++;

         console.log(
            `🔌 Attempting to connect to ${serverUrl} (attempt ${this.connectionAttempts}/${this.maxConnectionAttempts})`
         );

         try {
            // Create socket with enhanced options for auto-reconnect
            this.socket = io(serverUrl, {
               transports: ['websocket'],
               reconnection: true,
               autoConnect: true,
               path: '/socket.io',
               forceNew: true, // Force new connection to prevent conflicts
               timeout: 10000, // 10 second timeout
            });

            // Set up enhanced connection event handlers
            this.setupEnhancedConnectionListeners(resolve, reject);

            // Start connection attempt
            this.socket.connect();
         } catch (error) {
            console.error('❌ Socket creation failed:', error);
            reject(error);
         }
      });
   }

   // Enhanced connection event listeners
   private setupEnhancedConnectionListeners(
      resolve: () => void,
      reject: (error: any) => void
   ): void {
      if (!this.socket) return;

      // Connection successful
      const onConnect = () => {
         console.log('✅ Socket connected successfully');
         this.isConnected = true;
         this.connectionAttempts = 0;
         this.reconnectAttempts = 0;
         this.isReconnecting = false;

         // Clear any existing reconnection timers
         if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
         }

         // Start health monitoring
         this.startConnectionHealthCheck();

         // Authenticate user
         this.socket?.emit('join_user', {
            userId: this.currentUser?.id,
            username: this.currentUser?.name,
            displayName: this.currentUser?.name,
            profilePicture: null,
         });

         const onUserJoined = (data: any) => {
            console.log('👤 User joined successfully:', data);
            this.socket?.off('user_joined', onUserJoined);
            this.socket?.off('error', onJoinError);

            // Set up all other event listeners
            this.setupConnectionListeners();
            this.emit('connect');
            resolve();
         };

         const onJoinError = (error: any) => {
            console.error('❌ User join failed:', error);
            this.socket?.off('user_joined', onUserJoined);
            this.socket?.off('error', onJoinError);
            reject(error);
         };

         this.socket?.on('user_joined', onUserJoined);
         this.socket?.on('error', onJoinError);
      };

      // Connection error
      const onConnectError = (error: any) => {
         console.error('❌ Socket connection failed:', error);
         this.isConnected = false;
         this.emit('error', error);

         if (this.connectionAttempts >= this.maxConnectionAttempts) {
            console.error('❌ Maximum connection attempts reached');
            reject(new Error('Maximum connection attempts reached'));
         } else if (this.autoReconnectEnabled && !this.isReconnecting) {
            this.scheduleReconnection();
         } else {
            reject(error);
         }
      };

      // Socket.io built-in reconnection events
      const onReconnecting = (attemptNumber: number) => {
         console.log(`🔄 Socket.io reconnecting (attempt ${attemptNumber})`);
         this.isReconnecting = true;
         this.emit('reconnecting', attemptNumber);
      };

      const onReconnected = (attemptNumber: number) => {
         console.log(`✅ Socket.io reconnected successfully (after ${attemptNumber} attempts)`);
         this.isReconnecting = false;
         this.isConnected = true;
         this.reconnectAttempts = 0;

         // Start health monitoring
         this.startConnectionHealthCheck();

         // Restore connection state
         this.restoreConnectionState().catch((error) => {
            console.error('❌ Failed to restore connection state:', error);
         });

         this.emit('reconnected', attemptNumber);
      };

      const onReconnectError = (error: any) => {
         console.error('❌ Socket.io reconnection failed:', error);
         this.emit('reconnect_error', error);
      };

      const onReconnectFailed = () => {
         console.error('❌ Socket.io reconnection failed permanently');
         this.isReconnecting = false;
         this.isConnected = false;
         this.emit('reconnect_failed');
      };

      // Disconnect event
      const onDisconnect = (reason: string) => {
         console.log(`🔌 Socket disconnected: ${reason}`);
         this.isConnected = false;
         this.stopConnectionHealthCheck();

         // Only emit disconnect if it wasn't a manual disconnect
         if (!this.isManualDisconnect) {
            this.emit('disconnect', reason);
         }

         // Handle auto-reconnect for certain disconnect reasons
         if (this.autoReconnectEnabled && !this.isManualDisconnect && !this.isReconnecting) {
            const shouldReconnect = [
               'io server disconnect',
               'io client disconnect',
               'ping timeout',
               'transport close',
               'transport error',
            ].includes(reason);

            if (shouldReconnect) {
               console.log(`🔄 Auto-reconnect triggered due to: ${reason}`);
               this.scheduleReconnection();
            }
         }
      };

      // Pong response for health check
      const onPong = () => {
         this.lastPongReceived = Date.now();
         if (this.pingTimeout) {
            clearTimeout(this.pingTimeout);
            this.pingTimeout = null;
         }
      };

      // Bind all event listeners
      this.socket.on('connect', onConnect);
      this.socket.on('connect_error', onConnectError);
      this.socket.on('reconnecting', onReconnecting);
      this.socket.on('reconnect', onReconnected);
      this.socket.on('reconnect_error', onReconnectError);
      this.socket.on('reconnect_failed', onReconnectFailed);
      this.socket.on('disconnect', onDisconnect);
      this.socket.on('pong', onPong);

      // Store event listeners for cleanup
      this.socket.once('connect', () => {
         // Remove the resolve/reject handlers after successful connection
         this.socket?.off('connect_error', onConnectError);
      });
   }

   // Disconnect from server
   disconnect(): void {
      console.log('🔌 Manually disconnecting...');
      this.isManualDisconnect = true;
      this.autoReconnectEnabled = false;

      // Clear all timers
      if (this.reconnectTimer) {
         clearTimeout(this.reconnectTimer);
         this.reconnectTimer = null;
      }

      this.stopConnectionHealthCheck();

      // Clear joined rooms
      this.joinedRooms.clear();

      if (this.socket) {
         this.socket.disconnect();
         this.socket = null;
      }

      this.isConnected = false;
      this.isReconnecting = false;
      this.currentUser = null;
      this.serverUrl = '';
      this.connectionAttempts = 0;
      this.reconnectAttempts = 0;

      console.log('✅ Disconnected successfully');
   }

   // Get connection status
   getConnectionStatus(): boolean {
      return this.isConnected;
   }

   // Get socket for direct event emission
   getSocket(): Socket | null {
      return this.socket;
   }

   // Get reconnection status
   getReconnectionStatus(): {
      isReconnecting: boolean;
      attempts: number;
      maxAttempts: number;
      nextAttemptIn?: number;
      autoReconnectEnabled: boolean;
   } {
      const status = {
         isReconnecting: this.isReconnecting,
         attempts: this.reconnectAttempts,
         maxAttempts: this.maxReconnectAttempts,
         autoReconnectEnabled: this.autoReconnectEnabled,
      };

      // If there's a scheduled reconnection, calculate time remaining
      if (this.reconnectTimer) {
         const delay = Math.min(
            this.reconnectDelay * Math.pow(2, this.reconnectAttempts),
            this.maxReconnectDelay
         );
         return {
            ...status,
            nextAttemptIn: delay,
         };
      }

      return status;
   }

   // Force reconnection
   async forceReconnection(): Promise<void> {
      if (this.isConnected) {
         console.log('🔄 Forcing reconnection...');
         this.isManualDisconnect = false;
         this.autoReconnectEnabled = true;

         // Disconnect current connection
         if (this.socket) {
            this.socket.disconnect();
         }

         // Clear any existing timers
         if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
         }

         // Reset reconnection attempts
         this.reconnectAttempts = 0;

         // Start immediate reconnection
         await this.attemptReconnection();
      } else {
         console.log('🔄 Connection not active, starting fresh connection...');
         await this.attemptReconnection();
      }
   }

   // Enhanced reconnection scheduling
   private scheduleReconnection(): void {
      if (this.isReconnecting || this.reconnectTimer) {
         console.log('⏰ Reconnection already scheduled, skipping');
         return;
      }

      if (!this.autoReconnectEnabled) {
         console.log('⏰ Auto-reconnect disabled, skipping');
         return;
      }

      // Check online status before scheduling reconnection
      if (!this.isOnline) {
         console.log('🌐 Device is offline, skipping reconnection until network is restored');
         return;
      }

      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
         console.log('❌ Maximum reconnection attempts reached. Stopping auto-reconnect.');
         this.emit('max_reconnect_attempts_reached');
         return;
      }

      // Calculate delay with exponential backoff and jitter
      const baseDelay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts);
      const jitter = Math.random() * 0.3 * baseDelay; // Add up to 30% jitter
      const delay = Math.min(baseDelay + jitter, this.maxReconnectDelay);

      console.log(
         `⏰ Scheduling reconnection in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`
      );

      this.reconnectTimer = setTimeout(() => {
         this.reconnectTimer = null;
         this.reconnectAttempts++;
         this.attemptReconnection();
      }, delay);
   }

   // Enhanced reconnection attempt
   private async attemptReconnection(): Promise<void> {
      if (!this.currentUser || !this.serverUrl) {
         console.error('❌ Cannot reconnect: missing user or server URL');
         return;
      }

      if (this.isReconnecting) {
         console.log('🔄 Reconnection already in progress, skipping');
         return;
      }

      // Check online status before attempting reconnection
      if (!this.isOnline) {
         console.log('🌐 Device is offline, postponing reconnection until network is restored');
         return;
      }

      try {
         console.log(
            `🔄 Attempting reconnection (${this.reconnectAttempts}/${this.maxReconnectAttempts}) - Network: ${this.isOnline ? 'ONLINE' : 'OFFLINE'}`
         );
         this.isReconnecting = true;
         this.emit('reconnecting', this.reconnectAttempts);

         // Cleanup existing socket
         if (this.socket) {
            this.socket.disconnect();
            this.socket = null;
         }

         // Attempt new connection
         await this.connect(this.currentUser.id, this.currentUser.name, this.serverUrl);

         console.log('✅ Reconnection successful');
         this.isReconnecting = false;
         this.emit('reconnected', this.reconnectAttempts);
      } catch (error) {
         console.error('❌ Reconnection failed:', error);
         this.isReconnecting = false;
         this.emit('reconnect_error', error);

         // Only schedule next attempt if we're still online
         if (this.isOnline && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnection();
         } else if (!this.isOnline) {
            console.log(
               '🌐 Device went offline during reconnection, waiting for network restoration'
            );
         } else {
            console.error('❌ Maximum reconnection attempts reached');
            this.emit('max_reconnect_attempts_reached');
         }
      }
   }

   // Set up connection event listeners
   private setupConnectionListeners(): void {
      if (!this.socket) return;

      // Connection events
      this.socket.on('connect', () => {
         this.isConnected = true;
         this.connectionAttempts = 0;
         this.reconnectAttempts = 0;
         this.isReconnecting = false;

         // Clear any pending reconnection timers
         if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
         }

         this.emit('connect');
      });

      this.socket.on('disconnect', (reason) => {
         console.log('❌ Socket disconnected:', reason);
         this.isConnected = false;

         // Don't attempt reconnection for manual disconnections
         if (reason === 'io client disconnect') {
            this.emit('disconnect', reason);
            return;
         }

         // For other disconnections, attempt to reconnect
         this.scheduleReconnection();
         this.emit('disconnect', reason);
      });

      this.socket.on('connect_error', (error) => {
         console.error('❌ Socket connection error:', error);
         this.isConnected = false;
         this.connectionAttempts++;
         this.emit('error', error);

         if (this.connectionAttempts < this.maxConnectionAttempts) {
            this.scheduleReconnection();
         } else {
            console.error('❌ Max connection attempts reached');
            this.emit('max_reconnect_attempts_reached');
         }
      });

      // Error handling
      this.socket.on('error', (error: any) => {
         console.error('❌ Socket error during connection:', error);
         // Don't emit as global error if it's a specific operation error
         if (error.message && error.message.includes('not authenticated')) {
            this.reAuthenticate();
         }
      });

      // User events
      this.socket.on('user_joined', (data) => {});

      this.socket.on('joined_room', (data) => {
         // Add to joined rooms set
         this.joinedRooms.add(data.roomId);
      });

      // Online/Offline status events
      this.socket.on('user_online', (data) => {
         this.emit('user-status-changed', {
            userId: data.userId,
            username: data.username,
            isOnline: true,
            lastSeen: new Date(),
         });
      });

      this.socket.on('user_offline', (data) => {
         this.emit('user-status-changed', {
            userId: data.userId,
            username: data.username,
            isOnline: false,
            lastSeen: new Date(),
         });
      });

      this.socket.on('user_joined_room', (data) => {
         this.emit('user-joined', {
            userId: data.userId,
            username: data.username,
            isOnline: true,
         });

         // Handle user joined notification
         if (this.currentUser && data.userId !== this.currentUser.id) {
            console.log('📢 User joined:', data);
            // User joined notification is typically less important for the notification system
         }
      });

      this.socket.on('user_left_room', (data) => {
         this.emit('user-left', data.userId);
      });

      // Message events
      this.socket.on('new_message', (data) => {
         // Prevent duplicate messages
         const messageId = data.messageId || data.id;
         if (!messageId || this.processedMessageIds.has(messageId)) {
            return;
         }

         // Mark message as processed
         this.processedMessageIds.add(messageId);

         // Clean up old message IDs periodically (keep last 1000)
         if (this.processedMessageIds.size > 1000) {
            const idsArray = Array.from(this.processedMessageIds);
            this.processedMessageIds = new Set(idsArray.slice(-500)); // Keep last 500
         }

         // Convert server message format to our format
         const message: ChatMessage = {
            id: messageId,
            senderId: data.senderId,
            senderName: data.senderDisplayName || data.senderUsername,
            text: data.text || data.message, // Handle both 'message' and 'text' fields
            timestamp: new Date(data.timestamp),
            roomId: data.roomId,
            edited: data.edited || false,
            reactions: data.reactions || {}, // Initialize reactions object
         };

         // Update messages cache - also check for duplicates here
         if (!this.messages[message.roomId]) {
            this.messages[message.roomId] = [];
         }

         // Check if message already exists in room cache
         const existingMessage = this.messages[message.roomId].find((msg) => msg.id === message.id);
         if (existingMessage) {
            return;
         }

         // Add message to cache
         this.messages[message.roomId].push(message);

         this.emit('new-message', message);

         // Handle new message notification
         if (
            this.currentUser &&
            message.senderId !== this.currentUser.id &&
            this.notificationHandler
         ) {
            console.log('📢 Received new message:', message);
            this.notificationHandler({
               type: 'message',
               title: 'New Message',
               message: `${message.senderName || 'Someone'} sent you a message`,
               data: message,
            });
         }
      });

      // Reaction events
      this.socket.on('message_reaction', (data) => {
         // Convert action to added boolean for consistency
         const added = data.action === 'add' || data.added === true;

         this.emit('message-reaction', data.messageId, data.emoji, data.userId, added);

         // Handle message reaction notification
         if (
            this.currentUser &&
            data.userId !== this.currentUser.id &&
            added &&
            this.notificationHandler
         ) {
            console.log('📢 Received message reaction:', {
               messageId: data.messageId,
               emoji: data.emoji,
               userId: data.userId,
               added,
            });
            this.notificationHandler({
               type: 'reaction',
               title: 'Message Reaction',
               message: 'Someone reacted to your message',
               emoji: data.emoji,
               data: data,
            });
         }
      });

      // Typing events
      this.socket.on('user_typing', (data) => {
         this.emit('user-typing', data);
      });

      this.socket.on('user_stop_typing', (data) => {
         this.emit('user-stopped-typing', data.userId, data.roomId);
      });

      // Message edit/delete events
      this.socket.on('message_edited', (data) => {
         this.emit('message-edited', {
            id: data.messageId,
            text: data.newMessage,
            edited: true,
            editedAt: new Date(data.editedAt),
         } as ChatMessage);
      });

      this.socket.on('message_deleted', (data) => {
         this.emit('message-deleted', data.messageId, '');
      });

      // Group chat notification events
      this.socket.on('group_chat_notification', (notification) => {
         console.log('📢 Received group chat notification:', notification);
         this.emit('group-chat-notification', notification);

         // Handle group chat notification
         if (this.notificationHandler) {
            this.notificationHandler({
               type: 'group',
               title: 'Group Chat Invitation',
               message: `You've been added to "${notification.entityName}" group chat`,
               data: notification,
            });
         }
      });

      // New chat notification events (for first messages from contacts)
      this.socket.on('new_chat_notification', (notification) => {
         console.log('💬 Received new chat notification:', notification);
         this.emit('new-chat-notification', notification);

         // Handle new chat notification
         if (this.notificationHandler) {
            this.notificationHandler({
               type: 'message',
               title: notification.title || 'New Message',
               message:
                  notification.message ||
                  `${notification.data?.senderName || 'Someone'} sent you a message`,
               emoji: '💬',
               subtitle: notification.data?.messagePreview
                  ? `"${notification.data.messagePreview.substring(0, 50)}${notification.data.messagePreview.length > 50 ? '...' : ''}"`
                  : '',
               data: notification.data,
               priority: 'high', // First messages are high priority
               actions: [
                  {
                     label: 'Reply',
                     action: () => {
                        // Navigate to chat or open floating chat
                        if (notification.data?.roomId) {
                           window.location.href = `/communications?roomId=${notification.data.roomId}`;
                        }
                     },
                  },
                  {
                     label: 'Dismiss',
                     action: () => {
                        // Just dismiss the notification
                     },
                  },
               ],
            });
         }
      });

      // Handle acknowledgment events from backend
      this.socket.on('new_chat_notification_sent', (data) => {
         console.log('✅ New chat notification acknowledgment received:', data);
         this.emit('new-chat-notification-sent', data);
      });

      this.socket.on('new_chat_notification_error', (error) => {
         console.error('❌ New chat notification error:', error);
         this.emit('new-chat-notification-error', error);
      });

      // Enhanced handler for personal room notifications (event-trip-notification)
      this.socket.on('event-trip-notification', (notification) => {
         console.log('🔔 Personal notification received via room:', notification);
         console.log('🔔 Notification type:', notification.type);
         console.log('🔔 Full notification payload:', JSON.stringify(notification, null, 2));

         if (this.notificationHandler) {
            try {
               // Handle different notification types with enhanced display
               if (notification.type === 'TRIP_INVITATION') {
                  const tripData = notification.data || {};
                  console.log('✈️ Processing trip invitation notification');
                  console.log('✈️ Trip data:', tripData);
                  this.notificationHandler({
                     type: 'trip_invitation',
                     title: 'Trip Invitation',
                     message: `${notification.senderDisplayName} invited you to join "${tripData.tripTitle || 'a trip'}"`,
                     emoji: '✈️',
                     subtitle: `📅 ${tripData.tripDate || 'Date TBD'} • 📍 ${tripData.tripDestination || 'Location TBD'}`,
                     data: notification,
                  });
                  console.log('✈️ Trip invitation notification processed successfully');
               } else if (notification.type === 'EVENT_INVITATION') {
                  const eventData = notification.data || {};
                  console.log('🎉 Processing event invitation notification');
                  console.log('🎉 Event data:', eventData);
                  this.notificationHandler({
                     type: 'event_invitation',
                     title: 'Event Invitation',
                     message: `${notification.senderDisplayName} invited you to join "${eventData.eventTitle || 'an event'}"`,
                     emoji: '🎉',
                     subtitle: `📅 ${eventData.eventDate || 'Date TBD'} • 📍 ${eventData.eventLocation || 'Location TBD'}`,
                     data: notification,
                  });
                  console.log('🎉 Event invitation notification processed successfully');
               } else if (notification.type === 'TRIP_INVITATION_ACCEPTED') {
                  const tripData = notification.data || {};
                  this.notificationHandler({
                     type: 'trip_accepted',
                     title: 'Trip Invitation Accepted',
                     message: `${tripData.acceptedBy || 'Someone'} accepted your invitation to join "${tripData.tripTitle || 'your trip'}"`,
                     emoji: '✅',
                     subtitle: `📅 ${tripData.tripDate || 'Date TBD'} • 📍 ${tripData.tripDestination || 'Location TBD'}`,
                     data: notification,
                  });
               } else if (notification.type === 'EVENT_INVITATION_ACCEPTED') {
                  const eventData = notification.data || {};
                  this.notificationHandler({
                     type: 'event_accepted',
                     title: 'Event Invitation Accepted',
                     message: `${eventData.acceptedBy || 'Someone'} accepted your invitation to join "${eventData.eventTitle || 'your event'}"`,
                     emoji: '✅',
                     subtitle: `📅 ${eventData.eventDate || 'Date TBD'} • 📍 ${eventData.eventLocation || 'Location TBD'}`,
                     data: notification,
                  });
               } else {
                  // Generic notification handling
                  console.log('🔔 Processing generic notification');
                  console.log('🔔 Generic notification data:', notification);
                  this.notificationHandler({
                     type: notification.type || 'general',
                     title: notification.title || 'Notification',
                     message: notification.message || 'You have a new notification',
                     emoji: '🔔',
                     data: notification,
                  });
                  console.log('🔔 Generic notification processed successfully');
               }
            } catch (error) {
               console.error('❌ Error processing notification:', error);
            }
         }
      });
   }

   // Re-authenticate user after connection issues
   private async reAuthenticate(): Promise<void> {
      if (!this.currentUser || !this.socket) return;

      try {
         await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
               reject(new Error('Re-authentication timeout'));
            }, 5000);

            const onSuccess = () => {
               clearTimeout(timeout);
               this.socket?.off('user_joined', onSuccess);
               this.socket?.off('error', onError);
               resolve();
            };

            const onError = (error: any) => {
               clearTimeout(timeout);
               this.socket?.off('user_joined', onSuccess);
               this.socket?.off('error', onError);
               reject(error);
            };

            this.socket?.on('user_joined', onSuccess);
            this.socket?.on('error', onError);

            this.socket?.emit('join_user', {
               userId: this.currentUser?.id,
               username: this.currentUser?.name,
               displayName: this.currentUser?.name,
               profilePicture: null,
            });
         });
      } catch (error) {
         console.error('❌ Re-authentication failed:', error);
      }
   }

   // Restore connection state after reconnection
   private async restoreConnectionState(): Promise<void> {
      if (!this.currentUser || !this.socket) return;

      try {
         // First, re-authenticate the user
         await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
               reject(new Error('User re-authentication timeout'));
            }, 10000); // 10 second timeout

            const onSuccess = () => {
               clearTimeout(timeout);
               this.socket?.off('user_joined', onSuccess);
               this.socket?.off('error', onError);
               resolve();
            };

            const onError = (error: any) => {
               clearTimeout(timeout);
               this.socket?.off('user_joined', onSuccess);
               this.socket?.off('error', onError);
               reject(error);
            };

            this.socket?.on('user_joined', onSuccess);
            this.socket?.on('error', onError);

            this.socket?.emit('join_user', {
               userId: this.currentUser?.id,
               username: this.currentUser?.name,
               displayName: this.currentUser?.name,
               profilePicture: null,
            });
         });

         // Set user online status after successful reconnection
         try {
            await this.updateUserStatus(this.currentUser.id, this.currentUser.name, true);
         } catch (error) {
            console.warn('Failed to update user online status after reconnection:', error);
         }

         // Then, rejoin all previously joined rooms
         if (this.joinedRooms.size > 0) {
            const rejoinPromises = Array.from(this.joinedRooms).map(async (roomId) => {
               try {
                  await new Promise<void>((resolve, reject) => {
                     const timeout = setTimeout(() => {
                        reject(new Error(`Room rejoin timeout for ${roomId}`));
                     }, 5000); // 5 second timeout per room

                     const onSuccess = (data: any) => {
                        if (data.roomId === roomId) {
                           clearTimeout(timeout);
                           this.socket?.off('joined_room', onSuccess);
                           resolve();
                        }
                     };

                     this.socket?.on('joined_room', onSuccess);

                     this.socket?.emit('join_room', {
                        roomId,
                        userId: this.currentUser?.id || '',
                        roomType: roomId.startsWith('chat_') ? 'direct' : 'group',
                     });
                  });
               } catch (error) {
                  console.error(`❌ Failed to rejoin room ${roomId}:`, error);
                  // Remove from joinedRooms if we can't rejoin
                  this.joinedRooms.delete(roomId);
               }
            });

            // Wait for all room rejoin attempts to complete
            await Promise.allSettled(rejoinPromises);
         }
      } catch (error) {
         console.error('❌ Failed to restore connection state:', error);
         throw error;
      }
   }

   // Event listener management
   on<K extends keyof WebSocketEvents>(event: K, callback: WebSocketEvents[K]): void {
      if (!this.eventListeners.has(event)) {
         this.eventListeners.set(event, []);
      }
      this.eventListeners.get(event)!.push(callback);
   }

   off<K extends keyof WebSocketEvents>(event: K, callback: WebSocketEvents[K]): void {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
         const index = listeners.indexOf(callback);
         if (index > -1) {
            listeners.splice(index, 1);
         }
      }
   }

   private emit(event: string, ...args: any[]): void {
      const listeners = this.eventListeners.get(event);
      if (listeners) {
         listeners.forEach((listener) => {
            try {
               listener(...args);
            } catch (error) {
               console.error(`Error in WebSocket event listener for ${event}:`, error);
            }
         });
      }
   }

   // Room management methods
   async createRoom(name: string, description?: string, isPrivate = false): Promise<ChatRoom> {
      // For one-on-one chats, we should use createOneOnOneChat instead
      // This method is primarily for group rooms
      const roomId = `room_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Join the room (this will create it if it doesn't exist)
      const room = await this.joinRoom(roomId);

      // Return a properly formatted ChatRoom object
      return {
         id: roomId,
         name: name,
         description: description,
         createdBy: this.currentUser?.id || '',
         createdAt: new Date(),
         members: [this.currentUser?.id || ''],
         isPrivate: isPrivate,
      };
   }

   async createOneOnOneChat(otherUserId: string): Promise<ChatRoom> {
      const currentUserId = this.currentUser?.id;
      if (!currentUserId) {
         throw new Error('User not authenticated');
      }

      // Create consistent room ID for one-on-one chat (alphabetical order)
      const roomId =
         currentUserId < otherUserId
            ? `chat_${currentUserId}_${otherUserId}`
            : `chat_${otherUserId}_${currentUserId}`;

      // Check if chat room already exists in Firestore
      const existingRoomQuery = query(collection(db, 'chatRooms'), where('roomId', '==', roomId));
      const existingRoomSnapshot = await getDocs(existingRoomQuery);

      if (!existingRoomSnapshot.empty) {
         const existingRoom = existingRoomSnapshot.docs[0].data();
         console.log('One-on-one chat already exists:', roomId);

         // Join the existing room for real-time updates
         await this.joinRoom(roomId);

         return {
            id: roomId,
            name: existingRoom.name || 'Direct Chat',
            description: existingRoom.description || 'Direct message',
            createdBy: existingRoom.createdBy || currentUserId,
            createdAt: existingRoom.createdAt?.toDate() || new Date(),
            members: existingRoom.members || [currentUserId, otherUserId],
            isPrivate: true,
         };
      }

      // Join the room (this will create it if it doesn't exist)
      await this.joinRoom(roomId);

      // Enhanced user name resolution
      let otherUserName = `User ${otherUserId.substring(0, 8)}`;
      try {
         // Use the proper display name function
         otherUserName = await this.getProperDisplayName(otherUserId);
      } catch (userError) {
         console.error('Error fetching other user profile:', userError);
         // Keep the fallback name
      }

      // Create the chat room document in Firestore
      const chatRoomData = {
         roomId,
         roomType: 'direct',
         name: otherUserName,
         description: 'Direct message',
         createdAt: serverTimestamp(),
         createdBy: currentUserId,
         members: [currentUserId, otherUserId],
         isPrivate: true,
         lastActivity: serverTimestamp(),
      };

      try {
         // Use setDoc with roomId as document ID to match server expectations
         await setDoc(doc(db, 'chatRooms', roomId), chatRoomData);
         console.log(`Created one-on-one chat room: ${roomId}`);
      } catch (error) {
         console.error('Error creating chat room document:', error);
      }

      // Return a properly formatted ChatRoom object for one-on-one chat
      return {
         id: roomId,
         name: otherUserName,
         description: 'Direct message',
         createdBy: currentUserId,
         createdAt: new Date(),
         members: [currentUserId, otherUserId],
         isPrivate: true, // One-on-one chats are private
      };
   }

   async createTripGroupChat(tripId: string, tripTitle?: string): Promise<ChatRoom> {
      try {
         if (!this.currentUser) {
            throw new Error('User not authenticated');
         }

         // If tripTitle is not provided, fetch trip details
         let resolvedTripTitle = tripTitle;
         let tripData: any = null;

         if (!resolvedTripTitle) {
            const tripDoc = await getDoc(doc(db, 'trips', tripId));
            if (tripDoc.exists()) {
               tripData = tripDoc.data();
               resolvedTripTitle = tripData.title || tripData.destination || 'Trip';
            } else {
               resolvedTripTitle = 'Trip';
            }
         }

         const roomId = `trip_${tripId}`;

         // Check if group chat already exists
         const existingRoomQuery = query(
            collection(db, 'chatRooms'),
            where('roomId', '==', roomId)
         );
         const existingRoomSnapshot = await getDocs(existingRoomQuery);

         if (!existingRoomSnapshot.empty) {
            console.log('Trip group chat already exists');
            const existingRoom = existingRoomSnapshot.docs[0].data();
            return {
               id: roomId,
               name: existingRoom.name || resolvedTripTitle || 'Trip Chat', // Ensure string
               description: existingRoom.description,
               createdBy: existingRoom.createdBy,
               createdAt: existingRoom.createdAt?.toDate() || new Date(),
               members: existingRoom.members || [],
               isPrivate: false,
            };
         }

         // Fetch trip details if not already fetched
         if (!tripData) {
            const tripDoc = await getDoc(doc(db, 'trips', tripId));
            if (tripDoc.exists()) {
               tripData = tripDoc.data();
            }
         }

         // Get all participants from the trip
         const members = [this.currentUser.id]; // Start with current user

         if (tripData) {
            // Add trip participants/members
            if (tripData.participants && Array.isArray(tripData.participants)) {
               tripData.participants.forEach((participant: any) => {
                  const participantId = participant.userId || participant.id || participant;
                  if (participantId && !members.includes(participantId)) {
                     members.push(participantId);
                  }
               });
            }

            // Also check for members array
            if (tripData.members && Array.isArray(tripData.members)) {
               tripData.members.forEach((member: any) => {
                  const memberId = typeof member === 'string' ? member : member.userId || member.id;
                  if (memberId && !members.includes(memberId)) {
                     members.push(memberId);
                  }
               });
            }

            // Add trip creator if different
            if (tripData.createdBy && !members.includes(tripData.createdBy)) {
               members.push(tripData.createdBy);
            }

            // Add trip owner if different
            if (tripData.owner && !members.includes(tripData.owner)) {
               members.push(tripData.owner);
            }
         }

         // Create the group chat room in database
         const chatRoomData = {
            roomId,
            roomType: 'trip',
            name: resolvedTripTitle || 'Trip Chat', // Ensure we always have a string
            entityId: tripId,
            entityName: resolvedTripTitle || 'Trip',
            createdAt: serverTimestamp(),
            createdBy: this.currentUser.id,
            members,
            organizationId: tripData?.organizationId || null,
            isPersonal: !tripData?.organizationId,
            isPrivate: false,
            lastActivity: serverTimestamp(),
            // Only include metadata fields that are not undefined
            ...(tripData && {
               metadata: Object.fromEntries(
                  Object.entries({
                     destination: tripData.destination,
                     startDate: tripData.startDate,
                     endDate: tripData.endDate,
                     primaryDestination: tripData.primaryDestination,
                  }).filter(([key, value]) => value !== undefined)
               ),
            }),
         };

         // Use setDoc with roomId as document ID to match server expectations
         await setDoc(doc(db, 'chatRooms', roomId), chatRoomData);

         // Create welcome message
         await addDoc(collection(db, 'messages'), {
            messageId: `welcome_${roomId}_${Date.now()}`,
            roomId,
            senderId: 'system',
            senderUsername: 'System',
            senderDisplayName: 'System',
            message: `Welcome to "${resolvedTripTitle || 'Trip Chat'}"! All trip participants can coordinate and share updates here.`,
            text: `Welcome to "${resolvedTripTitle || 'Trip Chat'}"! All trip participants can coordinate and share updates here.`,
            timestamp: serverTimestamp(),
            messageType: 'system',
            edited: false,
            deleted: false,
         });

         // Try to join the room for real-time updates, but don't wait or fail if it times out
         this.joinRoom(roomId).catch((error) => {
            console.warn(`Failed to join trip room ${roomId} via WebSocket:`, error);
            // Continue anyway - group creation should still work
         });

         console.log(`Created trip group chat: ${roomId} with ${members.length} members`);

         // Return a properly formatted ChatRoom object immediately
         return {
            id: roomId,
            name: resolvedTripTitle || 'Trip Chat', // Ensure string
            description: `Group chat for ${resolvedTripTitle || 'Trip'}`,
            createdBy: this.currentUser.id,
            createdAt: new Date(),
            members,
            isPrivate: false,
         };
      } catch (error) {
         console.error('Error creating trip group chat:', error);
         throw error;
      }
   }

   /**
    * Create a group chat for an event and invite all participants
    */
   async createEventGroupChat(eventId: string): Promise<ChatRoom> {
      try {
         if (!this.currentUser) {
            throw new Error('User not authenticated');
         }

         // First, fetch the event details
         const eventDoc = await getDoc(doc(db, 'events', eventId));
         if (!eventDoc.exists()) {
            throw new Error('Event not found');
         }

         const eventData = eventDoc.data();
         const roomId = `event_${eventId}`;

         // Check if group chat already exists
         const existingRoomQuery = query(
            collection(db, 'chatRooms'),
            where('roomId', '==', roomId)
         );
         const existingRoomSnapshot = await getDocs(existingRoomQuery);

         if (!existingRoomSnapshot.empty) {
            console.log('Event group chat already exists');
            const existingRoom = existingRoomSnapshot.docs[0].data();
            return {
               id: roomId,
               name: existingRoom.name || eventData.title || 'Event Chat',
               description: existingRoom.description,
               createdBy: existingRoom.createdBy,
               createdAt: existingRoom.createdAt?.toDate() || new Date(),
               members: existingRoom.members || [],
               isPrivate: false,
            };
         }

         // Get all participants from the event
         const members = [this.currentUser.id]; // Start with current user

         // Add event participants
         if (eventData.participants && Array.isArray(eventData.participants)) {
            eventData.participants.forEach((participant: any) => {
               const participantId = participant.userId || participant.id || participant;
               if (participantId && !members.includes(participantId)) {
                  members.push(participantId);
               }
            });
         }

         // Add event creator if different
         if (eventData.createdBy && !members.includes(eventData.createdBy)) {
            members.push(eventData.createdBy);
         }

         // Create the group chat room in database
         const chatRoomData = {
            roomId,
            roomType: 'event',
            name: eventData.title || 'Event Chat', // Use just the event name
            entityId: eventId,
            entityName: eventData.title || 'Event',
            createdAt: serverTimestamp(),
            createdBy: this.currentUser.id,
            members,
            organizationId: eventData.organizationId || null,
            isPersonal: !eventData.organizationId,
            isPrivate: false,
            lastActivity: serverTimestamp(),
            // Only include metadata fields that are not undefined
            metadata: Object.fromEntries(
               Object.entries({
                  eventType: eventData.eventType,
                  startDate: eventData.startDate,
                  endDate: eventData.endDate,
                  location: eventData.location,
               }).filter(([key, value]) => value !== undefined)
            ),
         };

         // Use setDoc with roomId as document ID to match server expectations
         await setDoc(doc(db, 'chatRooms', roomId), chatRoomData);

         // Create welcome message
         await addDoc(collection(db, 'messages'), {
            messageId: `welcome_${roomId}_${Date.now()}`,
            roomId,
            senderId: 'system',
            senderUsername: 'System',
            senderDisplayName: 'System',
            message: `Welcome to "${eventData.title || 'Event Chat'}"! All event participants can coordinate and share updates here.`,
            text: `Welcome to "${eventData.title || 'Event Chat'}"! All event participants can coordinate and share updates here.`,
            timestamp: serverTimestamp(),
            messageType: 'system',
            edited: false,
            deleted: false,
         });

         // Try to join the room for real-time updates, but don't wait or fail if it times out
         this.joinRoom(roomId).catch((error) => {
            console.warn(`Failed to join event room ${roomId} via WebSocket:`, error);
            // Continue anyway - group creation should still work
         });

         console.log(`Created event group chat: ${roomId} with ${members.length} members`);

         // Return a properly formatted ChatRoom object immediately
         return {
            id: roomId,
            name: eventData.title || 'Event Chat', // Use just the event name
            description: `Group chat for ${eventData.title || 'Event'}`,
            createdBy: this.currentUser.id,
            createdAt: new Date(),
            members,
            isPrivate: false,
         };
      } catch (error) {
         console.error('Error creating event group chat:', error);
         throw error;
      }
   }

   /**
    * Get all events and trips for the current user to create group chats
    * Returns only events and trips where the user is the owner (role: 'owner' in members array)
    * Only owners can create group chats - editors and viewers cannot create group chats
    */
   async getAvailableEventsAndTrips(): Promise<{ events: any[]; trips: any[] }> {
      try {
         if (!this.currentUser) {
            return { events: [], trips: [] };
         }

         const events: any[] = [];
         const trips: any[] = [];

         // Fetch all events and filter by ownership
         const allEventsSnapshot = await getDocs(collection(db, 'events'));
         allEventsSnapshot.docs.forEach((doc) => {
            const data = doc.data();
            const members = data.members || [];
            
            // Check if current user is the owner in the members array
            const isOwner = members.some(
               (member: any) => 
                  member.userId === this.currentUser?.id && member.role === 'owner'
            );

            if (isOwner) {
               events.push({
                  id: doc.id,
                  title: data.title,
                  startDate: data.startDate,
                  endDate: data.endDate,
                  location: data.location,
                  participants: data.participants || [],
                  members: data.members || [],
                  organizationId: data.organizationId,
               });
            }
         });

         // Fetch all trips and filter by ownership
         const allTripsSnapshot = await getDocs(collection(db, 'trips'));
         allTripsSnapshot.docs.forEach((doc) => {
            const data = doc.data();
            const members = data.members || [];
            
            // Check if current user is the owner in the members array
            const isOwner = members.some(
               (member: any) => 
                  member.userId === this.currentUser?.id && member.role === 'owner'
            );

            if (isOwner) {
               trips.push({
                  id: doc.id,
                  title: data.title,
                  destination: data.destination,
                  startDate: data.startDate,
                  endDate: data.endDate,
                  participants: data.participants || [],
                  members: data.members || [],
                  organizationId: data.organizationId,
               });
            }
         });

         console.log(
            `📋 Found ${events.length} events and ${trips.length} trips where user is owner for group chat creation`
         );
         return { events, trips };
      } catch (error) {
         console.error('Error fetching available events and trips:', error);
         return { events: [], trips: [] };
      }
   }

   // Join a chat room
   async joinRoom(roomId: string): Promise<ChatRoom> {
      return new Promise((resolve, reject) => {
         if (!this.socket || !this.isConnected) {
            const error = new Error(
               'WebSocket not connected. Please check your connection and try again.'
            );

            reject(error);
            return;
         }

         if (!this.currentUser) {
            const error = new Error(
               'User not authenticated. Please refresh the page and try again.'
            );

            reject(error);
            return;
         }

         const onJoinedRoom = (data: any) => {
            clearTimeout(timeout);
            // Add to joined rooms set
            this.joinedRooms.add(data.roomId);

            this.socket?.off('joined_room', onJoinedRoom);
            this.socket?.off('error', onError);

            // Create a basic room object if not provided
            const room: ChatRoom = data.room || {
               id: roomId,
               name: data.roomName || `Room ${roomId}`,
               description: data.description || '',
               createdBy: data.createdBy || '',
               createdAt: new Date(),
               members: data.members || [this.currentUser?.id || ''],
               isPrivate: data.isPrivate || false,
            };

            this.emit('joined-room', room);
            resolve(room);
         };

         const onError = (error: any) => {
            clearTimeout(timeout);
            this.socket?.off('joined_room', onJoinedRoom);
            this.socket?.off('error', onError);

            // Parse error message for better user feedback
            let errorMessage = `Failed to join room: ${roomId}`;
            if (error && typeof error === 'object' && error.message) {
               errorMessage = error.message;
            } else if (typeof error === 'string') {
               errorMessage = error;
            }

            // Check for specific error types
            if (errorMessage.includes('not authenticated') || errorMessage.includes('refresh')) {
               errorMessage = 'Authentication expired. Please refresh the page and try again.';
            } else if (errorMessage.includes('not found')) {
               errorMessage = `Room ${roomId} not found or no longer exists.`;
            } else if (errorMessage.includes('permission')) {
               errorMessage = `You don't have permission to join room ${roomId}.`;
            }

            reject(new Error(errorMessage));
         };

         // Set up listeners
         this.socket?.once('joined_room', onJoinedRoom);
         this.socket?.once('error', onError);

         // Prepare join data
         const joinData = {
            roomId,
            userId: this.currentUser.id,
            roomType: roomId.startsWith('chat_') ? 'direct' : 'group',
         };

         // Send join room request
         this.socket?.emit('join_room', joinData);

         // Add timeout to prevent hanging
         const timeout = setTimeout(() => {
            this.socket?.off('joined_room', onJoinedRoom);
            this.socket?.off('error', onError);
            reject(new Error(`Room join timeout for ${roomId}. Please try again.`));
         }, 10000); // 10 second timeout
      });
   }

   async leaveRoom(roomId: string): Promise<void> {
      if (this.socket && this.isConnected) {
         this.socket.emit('leave_room', { roomId });
         // Remove from tracked rooms
         this.joinedRooms.delete(roomId);
      }
   }

   // Get all rooms for the current user
   async getRooms(): Promise<ChatRoom[]> {
      try {
         if (!this.currentUser) {
            console.warn('⚠️ No current user set for getRooms - cannot fetch chat rooms');
            return [];
         }

         const result = await this.getRoomsFromFirebase(this.currentUser.id, 50); // Get first 50 rooms
         return result.rooms;
      } catch (error) {
         console.error('Error fetching rooms:', error);
         return [];
      }
   }

   // New method to fetch rooms directly from Firebase without WebSocket dependency
   async getRoomsFromFirebase(
      userId: string,
      pageSize: number = 20,
      lastDoc?: any
   ): Promise<PaginatedRoomsResult> {
      try {
         console.log(
            '🔥 Fetching chat rooms directly from Firebase for user:',
            userId,
            'limit:',
            pageSize
         );

         // Create base query for rooms where user is a member
         let roomsQuery = query(
            collection(db, 'chatRooms'),
            where('members', 'array-contains', userId),
            orderBy('lastActivity', 'desc'),
            firestoreLimit(pageSize)
         );

         // Add pagination if lastDoc is provided
         if (lastDoc) {
            roomsQuery = query(
               collection(db, 'chatRooms'),
               where('members', 'array-contains', userId),
               orderBy('lastActivity', 'desc'),
               startAfter(lastDoc),
               firestoreLimit(pageSize)
            );
         }

         const roomsSnapshot = await getDocs(roomsQuery);
         const rooms: ChatRoom[] = [];
         const roomTypes: { [key: string]: number } = {};

         // Process rooms in batches to avoid too many concurrent queries
         const batchSize = 5;
         for (let i = 0; i < roomsSnapshot.docs.length; i += batchSize) {
            const batch = roomsSnapshot.docs.slice(i, i + batchSize);

            await Promise.all(
               batch.map(async (doc) => {
                  const data = doc.data();

                  // Track room types
                  const roomType = data.roomType || 'unknown';
                  roomTypes[roomType] = (roomTypes[roomType] || 0) + 1;

                  // Basic name resolution - avoid expensive queries
                  let resolvedName = data.name || data.roomName || 'Unknown Chat';

                  // Only do expensive name resolution for direct chats if name is generic
                  if (data.roomType === 'direct' && data.members?.length === 2) {
                     const otherUserId = data.members.find((id: string) => id !== userId);
                     if (
                        otherUserId &&
                        (!resolvedName ||
                           resolvedName === 'Unknown Chat' ||
                           resolvedName === 'Direct Chat' ||
                           resolvedName.startsWith('Room ') ||
                           resolvedName.startsWith('Chat with '))
                     ) {
                        // Try to get name from lastMessage first (cheapest option)
                        if (
                           data.lastMessage?.senderName &&
                           data.lastMessage.senderId === otherUserId
                        ) {
                           const senderName = data.lastMessage.senderName;
                           if (
                              senderName &&
                              senderName !== 'Unknown User' &&
                              !senderName.startsWith('User ')
                           ) {
                              resolvedName = senderName;
                           }
                        }

                        // If still generic, try user profile (but limit this expensive query)
                        if (resolvedName === 'Unknown Chat' || resolvedName === 'Direct Chat') {
                           try {
                              // Use the proper display name function
                              resolvedName = await this.getProperDisplayName(otherUserId);
                           } catch (error) {
                              console.warn('Failed to resolve user name:', error);
                              resolvedName = `User ${otherUserId.substring(0, 8)}`;
                           }
                        }
                     }
                  }

                  // Get last message if available
                  let lastMessage;
                  if (data.lastMessage) {
                     lastMessage = {
                        id: data.lastMessage.id || '',
                        senderId: data.lastMessage.senderId || '',
                        senderName: data.lastMessage.senderName || 'Unknown',
                        text: data.lastMessage.text || data.lastMessage.message || '',
                        timestamp: data.lastMessage.timestamp?.toDate() || new Date(),
                        roomId: doc.id,
                     };
                  }

                  const room: ChatRoom = {
                     id: doc.id,
                     name: resolvedName,
                     description: data.description || '',
                     createdBy: data.createdBy || '',
                     createdAt: data.createdAt?.toDate() || new Date(),
                     members: data.members || [],
                     isPrivate: data.isPrivate || data.roomType === 'direct' || false,
                     lastMessage,
                     unreadCount: data.unreadCount || 0,
                  };

                  // Load avatar URL efficiently - only for direct chats with timeout
                  if (data.roomType === 'direct' && data.members?.length === 2) {
                     try {
                        const otherUserId = data.members.find((id: string) => id !== userId);
                        if (otherUserId) {
                           // Use Promise.race to timeout after 800ms to avoid blocking
                           const avatarUrl = await Promise.race([
                              this.getChatRoomAvatarUrl(room, userId),
                              new Promise<null>((resolve) => setTimeout(() => resolve(null), 800)),
                           ]);
                           if (avatarUrl) {
                              room.avatarUrl = avatarUrl;
                           }
                        }
                     } catch (error) {
                        // Don't block loading if avatar fetch fails
                        console.warn(`Failed to get avatar URL for room ${room.id}:`, error);
                     }
                  }

                  rooms.push(room);
               })
            );
         }

         // Sort rooms by last activity (newest first)
         rooms.sort((a, b) => {
            const aTime = a.lastMessage?.timestamp || a.createdAt;
            const bTime = b.lastMessage?.timestamp || b.createdAt;
            return bTime.getTime() - aTime.getTime();
         });

         // Return paginated result
         const hasMore = roomsSnapshot.docs.length === pageSize;
         const lastDocument =
            roomsSnapshot.docs.length > 0
               ? roomsSnapshot.docs[roomsSnapshot.docs.length - 1]
               : undefined;

         return {
            rooms,
            hasMore,
            lastDoc: lastDocument,
            totalCount: rooms.length, // This is just the current batch, not total
         };
      } catch (error) {
         console.error('❌ Error fetching rooms from Firebase:', error);
         return {
            rooms: [],
            hasMore: false,
            lastDoc: undefined,
            totalCount: 0,
         };
      }
   }

   async cleanupInvalidRooms(): Promise<void> {
      if (!this.currentUser) {
         throw new Error('No current user');
      }

      try {
         const roomsQuery = query(
            collection(db, 'chatRooms'),
            where('members', 'array-contains', this.currentUser.id)
         );

         const roomsSnapshot = await getDocs(roomsQuery);
         const roomsToDelete: string[] = [];

         roomsSnapshot.docs.forEach((doc) => {
            const data = doc.data();

            // Mark for deletion if:
            // 1. No members
            // 2. Invalid room structure
            // 3. Very old rooms with no activity (older than 1 year)
            const oneYearAgo = new Date();
            oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

            if (
               !data.members ||
               data.members.length === 0 ||
               (!data.lastActivity && (!data.createdAt || data.createdAt.toDate() < oneYearAgo))
            ) {
               roomsToDelete.push(doc.id);
            }
         });

         // Delete invalid rooms
         await Promise.all(
            roomsToDelete.map(async (roomId) => {
               try {
                  await deleteDoc(doc(db, 'chatRooms', roomId));
               } catch (error) {
                  console.error('Error deleting room:', roomId, error);
               }
            })
         );

         if (roomsToDelete.length > 0) {
         }
      } catch (error) {
         console.error('Error during cleanup:', error);
         throw error;
      }
   }

   // Get loading states
   getLoadingStates() {
      return {
         sendingMessage: { ...this.loadingStates.sendingMessage },
         addingReaction: { ...this.loadingStates.addingReaction },
         removingReaction: { ...this.loadingStates.removingReaction },
      };
   }

   // Helper method to update loading state
   private updateLoadingState() {
      // Sync sendingMessages Set with loadingStates.sendingMessage object
      const newSendingState: { [roomId: string]: boolean } = {};

      // Set all sending rooms to true
      this.sendingMessages.forEach((roomId) => {
         newSendingState[roomId] = true;
      });

      // Update the loading states object
      this.loadingStates.sendingMessage = newSendingState;

      // Emit the updated loading states
      this.emit('loading-state-changed', this.getLoadingStates());
   }

   // Check if sending a message in a specific room
   isSendingMessage(roomId: string): boolean {
      return this.loadingStates.sendingMessage[roomId] || false;
   }

   // Check if adding reaction to specific message
   isAddingReaction(messageId: string, emoji: string): boolean {
      return this.loadingStates.addingReaction[messageId]?.[emoji] || false;
   }

   // Check if removing reaction from specific message
   isRemovingReaction(messageId: string, emoji: string): boolean {
      return this.loadingStates.removingReaction[messageId]?.[emoji] || false;
   }

   // Send a message to a room
   async sendMessage(roomId: string, text: string): Promise<void> {
      return new Promise((resolve, reject) => {
         // Validate inputs
         if (!roomId || !text?.trim()) {
            const error = new Error('Room ID and message text are required');
            console.error('❌ Invalid message data:', { roomId, text });
            reject(error);
            return;
         }

         // Check if connected and attempt reconnection if needed
         if (!this.socket || !this.isConnected) {
            // Try to reconnect first if socket exists but is not connected
            if (this.socket && this.socket.connected === false) {
               console.log('🔄 Socket exists but not connected, attempting to reconnect...');
               this.socket.connect();

               // Wait a moment for connection
               setTimeout(() => {
                  if (this.isConnected) {
                     console.log('✅ Reconnection successful, retrying message send...');
                     this.sendMessage(roomId, text).then(resolve).catch(reject);
                     return;
                  } else {
                     const error = new Error(
                        'WebSocket connection failed. Please check your internet connection and refresh the page.'
                     );
                     console.error('❌ Reconnection failed when trying to send message');
                     reject(error);
                     return;
                  }
               }, 3000);
               return;
            }

            const error = new Error(
               'WebSocket not connected. Please check your connection and try again.'
            );
            console.error('❌ WebSocket not connected when trying to send message');
            console.error('Socket state:', {
               socketExists: !!this.socket,
               isConnected: this.isConnected,
               socketConnected: this.socket?.connected,
            });
            reject(error);
            return;
         }

         // Check if already sending a message to this room
         if (this.sendingMessages.has(roomId)) {
            const error = new Error('Already sending a message to this room. Please wait.');
            console.warn('⚠️ Already sending message to room:', roomId);
            reject(error);
            return;
         }

         // Add to sending set
         this.sendingMessages.add(roomId);
         this.updateLoadingState();

         console.log(
            '📝 Message content:',
            text.substring(0, 100) + (text.length > 100 ? '...' : '')
         );
         console.log('🔗 Connection status:', {
            socketExists: !!this.socket,
            isConnected: this.isConnected,
            socketConnected: this.socket?.connected,
            currentUser: this.currentUser,
         });

         const timeoutId = setTimeout(() => {
            this.sendingMessages.delete(roomId);
            this.updateLoadingState();
            this.socket?.off('message_sent', onSuccess);
            this.socket?.off('error', onError);
            const error = new Error(
               'Message send timeout - no response from server after 10 seconds. Please check your connection.'
            );
            console.error('❌ Message send timeout for room:', roomId);
            reject(error);
         }, 10000); // 10 second timeout

         const onSuccess = (data: any) => {
            clearTimeout(timeoutId);
            this.sendingMessages.delete(roomId);
            this.updateLoadingState();
            this.socket?.off('error', onError);

            resolve();
         };

         const onError = (error: any) => {
            clearTimeout(timeoutId);
            this.sendingMessages.delete(roomId);
            this.updateLoadingState();
            this.socket?.off('message_sent', onSuccess);

            console.error('❌ Failed to send message:', error);
            console.error('Error details:', {
               message: error.message,
               type: typeof error,
               full: error,
            });

            // Parse error message for better user feedback
            let errorMessage = 'Failed to send message';
            if (error && typeof error === 'object' && error.message) {
               errorMessage = error.message;
            } else if (typeof error === 'string') {
               errorMessage = error;
            }

            // Check if it's an authentication error
            if (errorMessage.includes('not authenticated') || errorMessage.includes('refresh')) {
               errorMessage = 'Authentication expired. Please refresh the page and try again.';
            }

            reject(new Error(errorMessage));
         };

         // Set up listeners
         this.socket?.once('message_sent', onSuccess);
         this.socket?.once('error', onError);

         // Prepare message data
         const messageData = {
            roomId,
            text: text.trim(),
            messageType: 'text',
            senderId: this.currentUser?.id,
            senderName: this.currentUser?.name,
         };

         // Send the message
         this.socket?.emit('send_message', messageData);
      });
   }

   // Get messages between two users (for one-on-one chats)
   async getMessagesBetweenUsers(
      userId1: string,
      userId2: string,
      limit = 50,
      loadReactions = false // Make reactions optional for faster loading
   ): Promise<ChatMessage[]> {
      try {
         // Query messages where either user is the sender and the room contains both users
         const roomId = `chat_${[userId1, userId2].sort().join('_')}`;

         const messagesQuery = query(
            collection(db, 'messages'),
            where('roomId', '==', roomId),
            orderBy('timestamp', 'desc'),
            firestoreLimit(limit)
         );

         const messagesSnapshot = await getDocs(messagesQuery);
         const messages: ChatMessage[] = [];

         // Process messages without reactions first for faster loading
         messagesSnapshot.docs.forEach((doc) => {
            const data = doc.data();

            messages.push({
               id: doc.id,
               senderId: data.senderId || '',
               senderName: data.senderName || data.senderUsername || 'Unknown User',
               text: data.text || data.message || '',
               timestamp: data.timestamp?.toDate() || new Date(),
               roomId: data.roomId || roomId,
               edited: data.edited || false,
               editedAt: data.editedAt?.toDate(),
               reactions: {}, // Initialize empty reactions for now
            });
         });

         // Only load reactions if specifically requested (for better performance)
         if (loadReactions && messages.length > 0) {
            // Load reactions in batch for better performance
            try {
               const reactionPromises = messages.slice(0, 20).map(async (message) => {
                  // Limit to first 20 messages
                  try {
                     const reactionsSnapshot = await getDocs(
                        collection(db, 'messages', message.id, 'reactions')
                     );
                     const reactions: { [emoji: string]: string[] } = {};

                     reactionsSnapshot.forEach((reactionDoc) => {
                        const reaction = reactionDoc.data();
                        if (!reactions[reaction.emoji]) {
                           reactions[reaction.emoji] = [];
                        }
                        reactions[reaction.emoji].push(reaction.userId);
                     });

                     return { messageId: message.id, reactions };
                  } catch (error) {
                     console.warn(`Failed to load reactions for message ${message.id}:`, error);
                     return { messageId: message.id, reactions: {} };
                  }
               });

               const reactionResults = await Promise.all(reactionPromises);

               // Apply reactions to messages
               reactionResults.forEach(({ messageId, reactions }) => {
                  const messageIndex = messages.findIndex((msg) => msg.id === messageId);
                  if (messageIndex !== -1) {
                     messages[messageIndex].reactions = reactions;
                  }
               });
            } catch (error) {
               console.warn('Failed to load some reactions:', error);
            }
         }

         console.log(
            `✅ Loaded ${messages.length} messages between users ${loadReactions ? 'with' : 'without'} reactions for room ${roomId}`
         );
         return messages.reverse(); // Return in chronological order
      } catch (error) {
         console.error('Error fetching messages between users:', error);
         return [];
      }
   }

   // Get messages for a specific room
   async getRoomMessages(
      roomId: string,
      page = 1,
      limit = 50,
      loadReactions = false // Make reactions optional for faster loading
   ): Promise<ChatMessage[]> {
      try {
         const messagesQuery = query(
            collection(db, 'messages'),
            where('roomId', '==', roomId),
            orderBy('timestamp', 'desc'),
            firestoreLimit(limit)
         );

         const messagesSnapshot = await getDocs(messagesQuery);
         const messages: ChatMessage[] = [];

         // Process messages without reactions first for faster loading
         messagesSnapshot.docs.forEach((doc) => {
            const data = doc.data();

            messages.push({
               id: doc.id,
               senderId: data.senderId || '',
               senderName: data.senderName || data.senderUsername || 'Unknown User',
               text: data.text || data.message || '',
               timestamp: data.timestamp?.toDate() || new Date(),
               roomId: data.roomId || roomId,
               edited: data.edited || false,
               editedAt: data.editedAt?.toDate(),
               reactions: {}, // Initialize empty reactions for now
            });
         });

         // Only load reactions if specifically requested (for better performance)
         if (loadReactions && messages.length > 0) {
            // Load reactions in batch for better performance
            try {
               const reactionPromises = messages.slice(0, 20).map(async (message) => {
                  // Limit to first 20 messages
                  try {
                     const reactionsSnapshot = await getDocs(
                        collection(db, 'messages', message.id, 'reactions')
                     );
                     const reactions: { [emoji: string]: string[] } = {};

                     reactionsSnapshot.forEach((reactionDoc) => {
                        const reaction = reactionDoc.data();
                        if (!reactions[reaction.emoji]) {
                           reactions[reaction.emoji] = [];
                        }
                        reactions[reaction.emoji].push(reaction.userId);
                     });

                     return { messageId: message.id, reactions };
                  } catch (error) {
                     console.warn(`Failed to load reactions for message ${message.id}:`, error);
                     return { messageId: message.id, reactions: {} };
                  }
               });

               const reactionResults = await Promise.all(reactionPromises);

               // Apply reactions to messages
               reactionResults.forEach(({ messageId, reactions }) => {
                  const messageIndex = messages.findIndex((msg) => msg.id === messageId);
                  if (messageIndex !== -1) {
                     messages[messageIndex].reactions = reactions;
                  }
               });
            } catch (error) {
               console.warn('Failed to load some reactions:', error);
            }
         }

         console.log(
            `✅ Loaded ${messages.length} messages ${loadReactions ? 'with' : 'without'} reactions for room ${roomId}`
         );
         return messages.reverse(); // Return in chronological order
      } catch (error) {
         console.error('Error fetching room messages:', error);
         return [];
      }
   }

   // Clear cache and refresh (placeholder implementation)
   async clearCacheAndRefresh(): Promise<void> {
      try {
         // Clear all caches
         this.messages = {};
         this.processedMessageIds.clear();

         // Restore connection state if we were connected
         if (this.isConnected && this.currentUser) {
            const roomsToRejoin = Object.keys(this.messages);

            // Re-join all rooms the user was in
            await Promise.all(
               roomsToRejoin.map(async (roomId) => {
                  try {
                     await this.joinRoom(roomId);
                  } catch (error) {
                     console.error(`Failed to rejoin room ${roomId}:`, error);
                  }
               })
            );
         }
      } catch (error) {
         console.error('Failed to clear cache and refresh:', error);
         throw error;
      }
   }

   // Edit message (placeholder implementation)
   async editMessage(messageId: string, newText: string, roomId?: string): Promise<void> {
      if (!this.socket || !this.isConnected) {
         throw new Error('WebSocket not connected');
      }

      if (!this.currentUser) {
         throw new Error('No current user');
      }

      console.log('✏️ Editing message:', messageId, 'New text:', newText);

      // Use provided roomId or try to find it
      let targetRoomId = roomId;
      if (!targetRoomId) {
         const foundRoomId = this.findRoomIdForMessage(messageId);
         if (!foundRoomId) {
            throw new Error('Could not find room for message. Please provide roomId parameter.');
         }
         targetRoomId = foundRoomId;
      }

      this.socket.emit('edit_message', {
         messageId,
         newText,
         roomId: targetRoomId,
      });
   }

   // Delete message (placeholder implementation)
   async deleteMessage(messageId: string, roomId?: string): Promise<void> {
      if (!this.socket || !this.isConnected) {
         throw new Error('WebSocket not connected');
      }

      if (!this.currentUser) {
         throw new Error('No current user');
      }

      console.log('🗑️ Deleting message:', messageId);

      // Use provided roomId or try to find it
      let targetRoomId = roomId;
      if (!targetRoomId) {
         const foundRoomId = this.findRoomIdForMessage(messageId);
         if (!foundRoomId) {
            throw new Error('Could not find room for message. Please provide roomId parameter.');
         }
         targetRoomId = foundRoomId;
      }

      this.socket.emit('delete_message', {
         messageId,
         roomId: targetRoomId,
      });
   }

   // React to a message
   async reactToMessage(messageId: string, emoji: string, roomId?: string): Promise<void> {
      if (!this.socket || !this.isConnected) {
         throw new Error('WebSocket not connected');
      }

      if (!this.currentUser) {
         throw new Error('No current user');
      }

      if (!messageId || !emoji) {
         throw new Error('Message ID and emoji are required');
      }

      // Check if already adding this reaction
      if (this.loadingStates.addingReaction[messageId]?.[emoji]) {
         throw new Error('Already adding this reaction');
      }

      // Initialize reaction loading state if needed
      if (!this.loadingStates.addingReaction[messageId]) {
         this.loadingStates.addingReaction[messageId] = {};
      }

      // Set loading state
      this.loadingStates.addingReaction[messageId][emoji] = true;
      this.emit('loading-state-changed', this.getLoadingStates());

      // Use provided roomId or try to find it
      let targetRoomId = roomId;
      if (!targetRoomId) {
         const foundRoomId = this.findRoomIdForMessage(messageId);
         if (!foundRoomId) {
            // Clear loading state on error
            this.loadingStates.addingReaction[messageId][emoji] = false;
            this.emit('loading-state-changed', this.getLoadingStates());
            throw new Error('Could not find room for message. Please provide roomId parameter.');
         }
         targetRoomId = foundRoomId;
      }

      try {
         this.socket.emit('add_reaction', {
            messageId,
            emoji,
            roomId: targetRoomId,
         });

         // Wait for confirmation or timeout
         return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
               // Clear loading state on timeout
               this.loadingStates.addingReaction[messageId][emoji] = false;
               this.emit('loading-state-changed', this.getLoadingStates());
               reject(new Error('Reaction add timeout'));
            }, 5000); // 5 second timeout

            const onSuccess = (data: any) => {
               clearTimeout(timeout);
               this.socket?.off('reaction_added', onSuccess);
               this.socket?.off('error', onError);

               // Clear loading state on success
               this.loadingStates.addingReaction[messageId][emoji] = false;
               this.emit('loading-state-changed', this.getLoadingStates());
               resolve();
            };

            const onError = (error: any) => {
               clearTimeout(timeout);
               this.socket?.off('reaction_added', onSuccess);
               this.socket?.off('error', onError);

               // Clear loading state on error
               this.loadingStates.addingReaction[messageId][emoji] = false;
               this.emit('loading-state-changed', this.getLoadingStates());
               reject(new Error(error.message || 'Failed to add reaction'));
            };

            this.socket?.on('reaction_added', onSuccess);
            this.socket?.on('error', onError);
         });
      } catch (error) {
         // Clear loading state on error
         this.loadingStates.addingReaction[messageId][emoji] = false;
         this.emit('loading-state-changed', this.getLoadingStates());
         console.error('❌ Error adding reaction:', error);
         throw error;
      }
   }

   // Remove reaction from a message
   async removeReaction(messageId: string, emoji: string, roomId?: string): Promise<void> {
      if (!this.socket || !this.isConnected) {
         throw new Error('WebSocket not connected');
      }

      if (!this.currentUser) {
         throw new Error('No current user');
      }

      if (!messageId || !emoji) {
         throw new Error('Message ID and emoji are required');
      }

      // Check if already removing this reaction
      if (this.loadingStates.removingReaction[messageId]?.[emoji]) {
         throw new Error('Already removing this reaction');
      }

      // Initialize reaction loading state if needed
      if (!this.loadingStates.removingReaction[messageId]) {
         this.loadingStates.removingReaction[messageId] = {};
      }

      // Set loading state
      this.loadingStates.removingReaction[messageId][emoji] = true;
      this.emit('loading-state-changed', this.getLoadingStates());

      // Use provided roomId or try to find it
      let targetRoomId = roomId;
      if (!targetRoomId) {
         const foundRoomId = this.findRoomIdForMessage(messageId);
         if (!foundRoomId) {
            // Clear loading state on error
            this.loadingStates.removingReaction[messageId][emoji] = false;
            this.emit('loading-state-changed', this.getLoadingStates());
            throw new Error('Could not find room for message. Please provide roomId parameter.');
         }
         targetRoomId = foundRoomId;
      }

      try {
         this.socket.emit('remove_reaction', {
            messageId,
            emoji,
            roomId: targetRoomId,
         });

         // Wait for confirmation or timeout
         return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
               // Clear loading state on timeout
               this.loadingStates.removingReaction[messageId][emoji] = false;
               this.emit('loading-state-changed', this.getLoadingStates());
               reject(new Error('Reaction remove timeout'));
            }, 5000); // 5 second timeout

            const onSuccess = (data: any) => {
               clearTimeout(timeout);
               this.socket?.off('reaction_removed', onSuccess);
               this.socket?.off('error', onError);

               // Clear loading state on success
               this.loadingStates.removingReaction[messageId][emoji] = false;
               this.emit('loading-state-changed', this.getLoadingStates());
               resolve();
            };

            const onError = (error: any) => {
               clearTimeout(timeout);
               this.socket?.off('reaction_removed', onSuccess);
               this.socket?.off('error', onError);

               // Clear loading state on error
               this.loadingStates.removingReaction[messageId][emoji] = false;
               this.emit('loading-state-changed', this.getLoadingStates());
               reject(new Error(error.message || 'Failed to remove reaction'));
            };

            this.socket?.on('reaction_removed', onSuccess);
            this.socket?.on('error', onError);
         });
      } catch (error) {
         // Clear loading state on error
         this.loadingStates.removingReaction[messageId][emoji] = false;
         this.emit('loading-state-changed', this.getLoadingStates());
         console.error('❌ Error removing reaction:', error);
         throw error;
      }
   }

   // Helper method to find room ID for a message
   private findRoomIdForMessage(messageId: string): string | null {
      for (const [roomId, messages] of Object.entries(this.messages)) {
         if (messages.some((msg) => msg.id === messageId)) {
            return roomId;
         }
      }
      return null;
   }

   // Method to update internal message cache (for external use)
   updateMessageCache(roomId: string, messages: ChatMessage[]): void {
      this.messages[roomId] = messages;
   }

   // Start typing indicator (placeholder implementation)
   startTyping(roomId: string): void {
      if (this.socket && this.isConnected && this.currentUser) {
         this.socket.emit('start_typing', { roomId, userId: this.currentUser.id });
      }
   }

   // Stop typing indicator (placeholder implementation)
   stopTyping(roomId: string): void {
      if (this.socket && this.isConnected && this.currentUser) {
         this.socket.emit('stop_typing', { roomId, userId: this.currentUser.id });
      }
   }

   // Helper method to get appropriate avatar URL for chat rooms
   async getChatRoomAvatarUrl(room: ChatRoom, currentUserId?: string): Promise<string | null> {
      try {
         // For direct chats (one-on-one), get the other user's profile photo
         if (room.isPrivate && room.members.length === 2) {
            const otherUserId = room.members.find((id) => id !== currentUserId);
            if (otherUserId) {
               return await this.getUserAvatarUrl(otherUserId);
            }
         }

         // For trip chats, get trip cover photo
         if (room.id.startsWith('trip_')) {
            const tripId = room.id.replace('trip_', '');
            return await this.getTripCoverUrl(tripId);
         }

         // For event chats, get event cover image
         if (room.id.startsWith('event_')) {
            const eventId = room.id.replace('event_', '');
            return await this.getEventCoverUrl(eventId);
         }

         return null;
      } catch (error) {
         console.error('Error getting chat room avatar URL:', error);
         return null;
      }
   }

   // Get user profile photo URL - make this public
   async getUserAvatarUrl(userId: string): Promise<string | null> {
      try {
         // Try userProfiles collection first - using uid field, not userId
         const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
         if (userProfileDoc.exists()) {
            const userProfile = userProfileDoc.data();
            if (userProfile.photoURL) {
               return userProfile.photoURL;
            }
         }

         // Fallback to users collection
         const userQuery = query(collection(db, 'users'), where('uid', '==', userId));
         const userSnapshot = await getDocs(userQuery);

         if (!userSnapshot.empty) {
            const userData = userSnapshot.docs[0].data();
            if (userData.photoURL) {
               return userData.photoURL;
            }
         }

         return null;
      } catch (error) {
         console.error('Error fetching user avatar URL:', error);
         return null;
      }
   }

   // Get trip cover photo URL
   private async getTripCoverUrl(tripId: string): Promise<string | null> {
      try {
         const tripDoc = await getDoc(doc(db, 'trips', tripId));
         if (tripDoc.exists()) {
            const tripData = tripDoc.data();
            return tripData.coverPhotoUrl || tripData.coverImageUrl || null;
         }
         return null;
      } catch (error) {
         console.error('Error fetching trip cover URL:', error);
         return null;
      }
   }

   // Get event cover image URL
   private async getEventCoverUrl(eventId: string): Promise<string | null> {
      try {
         const eventDoc = await getDoc(doc(db, 'events', eventId));
         if (eventDoc.exists()) {
            const eventData = eventDoc.data();
            return eventData.coverImageUrl || eventData.coverPhotoUrl || null;
         }
         return null;
      } catch (error) {
         console.error('Error fetching event cover URL:', error);
         return null;
      }
   }

   // Get message sender avatar URL
   async getMessageSenderAvatarUrl(
      message: ChatMessage,
      roomType?: string
   ): Promise<string | null> {
      try {
         // For system messages, return null
         if (message.senderId === 'system') {
            return null;
         }

         // Get user's profile photo
         return await this.getUserAvatarUrl(message.senderId);
      } catch (error) {
         console.error('Error getting message sender avatar URL:', error);
         return null;
      }
   }

   // Notify group chat members about events (like new group chat creation)
   async notifyGroupChatMembers(
      roomId: string,
      members: string[],
      notification: {
         type: 'group_chat_created' | 'group_chat_updated';
         entityType: 'event' | 'trip';
         entityName: string;
         roomId: string;
         createdBy: string;
      }
   ): Promise<void> {
      try {
         if (!this.socket || !this.isConnected) {
            console.log('WebSocket not connected, skipping member notification');
            return;
         }

         if (!this.currentUser) {
            console.log('No current user, skipping member notification');
            return;
         }

         console.log(
            `📢 Notifying ${members.length} members about ${notification.type} for ${notification.entityType}: ${notification.entityName}`
         );

         // Emit notification to all members except the creator
         const membersToNotify = members.filter((memberId) => memberId !== this.currentUser!.id);

         if (membersToNotify.length > 0) {
            this.socket.emit('group_chat_notification', {
               ...notification,
               members: membersToNotify,
               timestamp: new Date().toISOString(),
            });
         }
      } catch (error) {
         console.error('Error notifying group chat members:', error);
         // Don't throw error - notification failure shouldn't break group chat creation
      }
   }

   // Load avatars for group chats lazily (called after room list is loaded)
   async loadAvatarsForRooms(rooms: ChatRoom[], currentUserId: string): Promise<void> {
      try {
         // Process rooms in batches to avoid too many concurrent requests
         const batchSize = 3;
         for (let i = 0; i < rooms.length; i += batchSize) {
            const batch = rooms.slice(i, i + batchSize);

            await Promise.all(
               batch.map(async (room) => {
                  try {
                     // Skip if avatar already loaded
                     if (room.avatarUrl) {
                        return;
                     }

                     // Load avatar with timeout to avoid blocking
                     const avatarUrl = await Promise.race([
                        this.getChatRoomAvatarUrl(room, currentUserId),
                        new Promise<null>((resolve) => setTimeout(() => resolve(null), 1500)),
                     ]);

                     if (avatarUrl) {
                        room.avatarUrl = avatarUrl;
                     }
                  } catch (error) {
                     console.warn(`Failed to load avatar for room ${room.id}:`, error);
                  }
               })
            );
         }
      } catch (error) {
         console.error('Error loading avatars for rooms:', error);
      }
   }

   // Get user online status from Firebase
   async getUserOnlineStatus(userId: string): Promise<UserStatus | null> {
      try {
         const { collection, doc, getDoc } = await import('firebase/firestore');
         const { db } = await import('../firebase/config');

         const userDoc = await getDoc(doc(db, 'users', userId));
         if (userDoc.exists()) {
            const userData = userDoc.data();
            return {
               userId,
               username:
                  userData.displayName ||
                  userData.fullName ||
                  userData.email?.split('@')[0] ||
                  'Unknown',
               isOnline: userData.isOnline || false,
               lastSeen: userData.lastSeen?.toDate?.() || new Date(),
            };
         }
         return null;
      } catch (error) {
         console.error('Error fetching user online status:', error);
         return null;
      }
   }

   // Get multiple users' online status from Firebase
   async getMultipleUsersOnlineStatus(userIds: string[]): Promise<UserStatus[]> {
      try {
         const statusPromises = userIds.map((userId) => this.getUserOnlineStatus(userId));
         const results = await Promise.all(statusPromises);
         return results.filter((status): status is UserStatus => status !== null);
      } catch (error) {
         console.error('Error fetching multiple users online status:', error);
         return [];
      }
   }

   // Emit user online status to server
   setUserOnline(userId: string, username: string): void {
      if (this.socket && this.isConnected) {
         this.socket.emit('user_online', { userId, username });
      }
   }

   // Emit user offline status to server
   setUserOffline(userId: string, username: string): void {
      if (this.socket && this.isConnected) {
         this.socket.emit('user_offline', { userId, username });
      }
   }

   // Update user status in Firebase and emit to server
   async updateUserStatus(userId: string, username: string, isOnline: boolean): Promise<void> {
      // Check throttling to prevent rapid status updates
      const now = Date.now();
      const lastUpdate = this.lastStatusUpdate[userId];

      if (
         lastUpdate &&
         lastUpdate.isOnline === isOnline &&
         now - lastUpdate.timestamp < this.statusUpdateThrottle
      ) {
         return;
      }

      // Update throttling record
      this.lastStatusUpdate[userId] = {
         isOnline,
         timestamp: now,
      };

      try {
         // Update status in Firebase first (this should always work)
         const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');
         const { db } = await import('../firebase/config');

         await updateDoc(doc(db, 'users', userId), {
            isOnline,
            lastSeen: serverTimestamp(),
         });
      } catch (error) {
         console.error('Error updating user status in Firebase:', error);
      }

      // Emit status to server (if WebSocket is connected)
      try {
         if (this.socket && this.isConnected) {
            if (isOnline) {
               this.setUserOnline(userId, username);
            } else {
               this.setUserOffline(userId, username);
            }
         }
      } catch (error) {
         console.error('Error emitting user status to server:', error);
      }
   }

   // Get current user
   getCurrentUser(): { id: string; name: string } | null {
      return this.currentUser;
   }

   // Public method to get proper display name
   async getDisplayName(userId: string, fallbackEmail?: string): Promise<string> {
      return this.getProperDisplayName(userId, fallbackEmail);
   }

   // Helper function to get proper display name from user profile
   private async getProperDisplayName(userId: string, fallbackEmail?: string): Promise<string> {
      try {
         // First, try to get from userProfiles collection (most up-to-date) - use direct doc access
         const userProfileDoc = await getDoc(doc(db, 'userProfiles', userId));
         if (userProfileDoc.exists()) {
            const profileData = userProfileDoc.data();
            const displayName =
               profileData.displayName || profileData.fullName || profileData.firstName;
            if (displayName && displayName !== 'Unknown User' && !displayName.startsWith('User ')) {
               return displayName;
            }
         }

         // Fallback to users collection
         const userDoc = await getDoc(doc(db, 'users', userId));
         if (userDoc.exists()) {
            const userData = userDoc.data();
            const displayName = userData.displayName || userData.fullName || userData.firstName;
            if (displayName && displayName !== 'Unknown User' && !displayName.startsWith('User ')) {
               return displayName;
            }
         }

         // Final fallback to email
         return fallbackEmail?.split('@')[0] || 'User';
      } catch (error) {
         console.error('Error fetching proper display name:', error);
         return fallbackEmail?.split('@')[0] || 'User';
      }
   }

   // Notify user about new chat (for first messages)
   notifyUserNewChat(
      recipientId: string,
      roomId: string,
      senderName: string,
      messagePreview: string
   ): boolean {
      if (this.socket && this.isConnected) {
         this.socket.emit('notify_user_new_chat', {
            recipientId,
            roomId,
            senderName,
            messagePreview,
         });
         console.log(`📤 Sent new chat notification for room: ${roomId} to user: ${recipientId}`);
         return true;
      } else {
         console.warn('⚠️ Cannot send new chat notification - WebSocket not connected');
         return false;
      }
   }
}

// Create and export a singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;
